import { supabase } from '@/integrations/supabase/client';

export interface AnalyticsEvent {
  event_name: string;
  event_data?: Record<string, any>;
  user_id?: string;
  session_id?: string;
}

// Declare gtag global
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

/**
 * Serviço de Analytics para rastreamento de eventos e comportamento do usuário
 * ✅ FASE 2: Integrado com Google Analytics 4 + Supabase
 */
class AnalyticsService {
  private sessionId: string;
  private userId: string | null = null;
  private GA4_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.initializeUser();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private async initializeUser() {
    const { data: { user } } = await supabase.auth.getUser();
    this.userId = user?.id || null;

    // Listen for auth changes
    supabase.auth.onAuthStateChange((_, session) => {
      this.userId = session?.user?.id || null;
    });
  }

  /**
   * Track generic event
   * ✅ FASE 2: Envia para GA4 + Supabase
   */
  async trackEvent(eventName: string, eventData?: Record<string, any>) {
    const event: AnalyticsEvent = {
      event_name: eventName,
      event_data: eventData,
      user_id: this.userId || undefined,
      session_id: this.sessionId,
    };

    // Log locally in development
    if (import.meta.env.DEV) {
      console.log('📊 Analytics Event:', event);
    }

    // ✅ Enviar para Google Analytics 4
    if (window.gtag && this.GA4_ID) {
      try {
        window.gtag('event', eventName, {
          ...eventData,
          user_id: this.userId,
          session_id: this.sessionId,
        });
      } catch (error) {
        console.warn('Failed to send event to GA4:', error);
      }
    }

    // ✅ Store in Supabase database (fire and forget)
    try {
      await supabase.from('analytics_events').insert({
        event_name: eventName,
        event_data: eventData || {},
        user_id: this.userId,
        session_id: this.sessionId,
      });
    } catch (error) {
      console.warn('Failed to store analytics event in DB:', error);
    }
  }

  /**
   * Track page view
   * ✅ FASE 2: Envia para GA4 + Supabase
   */
  trackPageView(pageName: string, metadata?: Record<string, any>) {
    // GA4 page_view específico
    if (window.gtag && this.GA4_ID) {
      window.gtag('config', this.GA4_ID, {
        page_path: pageName,
        page_title: metadata?.title || pageName,
      });
    }
    
    this.trackEvent('page_view', {
      page: pageName,
      ...metadata,
    });
  }

  /**
   * Track user action
   */
  trackAction(action: string, category: string, metadata?: Record<string, any>) {
    this.trackEvent('user_action', {
      action,
      category,
      ...metadata,
    });
  }

  /**
   * Track feature usage
   */
  trackFeatureUsage(featureName: string, metadata?: Record<string, any>) {
    this.trackEvent('feature_usage', {
      feature: featureName,
      ...metadata,
    });
  }

  /**
   * Track onboarding progress
   */
  trackOnboardingStep(step: string, completed: boolean) {
    this.trackEvent('onboarding_progress', {
      step,
      completed,
    });
  }

  /**
   * Track habit completion
   */
  trackHabitCompletion(habitId: string, habitTitle: string, streakCount: number) {
    this.trackEvent('habit_completed', {
      habit_id: habitId,
      habit_title: habitTitle,
      streak: streakCount,
    });
  }

  /**
   * Track goal creation
   */
  trackGoalCreation(goalId: string, pilar: string) {
    this.trackEvent('goal_created', {
      goal_id: goalId,
      pilar,
    });
  }

  /**
   * Track challenge participation
   */
  trackChallengeJoin(challengeId: string, challengeTitle: string) {
    this.trackEvent('challenge_joined', {
      challenge_id: challengeId,
      challenge_title: challengeTitle,
    });
  }

  /**
   * Track engagement metrics
   */
  trackEngagement(type: 'session_start' | 'session_end', duration?: number) {
    this.trackEvent('engagement', {
      type,
      duration_seconds: duration,
    });
  }
}

export const analyticsService = new AnalyticsService();
